import React, { useState, useEffect, useRef } from 'react';
import './ControlPanel.css';
import { format, subDays, subMonths, subYears } from 'date-fns';

function ControlPanel({
  // Period props
  period,
  compareMode,
  onPeriodChange,
  onCompareModeChange,
  // Sort props
  sortBy,
  onSortChange,
  // Group props
  groups,
  activeGroup,
  onGroupChange,
  accountsCount,
  ungroupedAccountsCount,
  // Visibility
  showGroups = true,
  showSort = true
}) {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const lastScrollY = useRef(0);
  const scrollVelocityRef = useRef(0);
  const scrollTimeoutRef = useRef(null);

  // Auto-hide filters on active downward scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY.current;

      // Detect downward scroll
      if (scrollDelta > 0) {
        scrollVelocityRef.current = scrollDelta;

        // If user scrolls down fast enough, collapse filters
        if (scrollDelta > 5 && !isCollapsed) {
          setIsCollapsed(true);
        }

        // Clear any pending timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Reset velocity after scroll stops
        scrollTimeoutRef.current = setTimeout(() => {
          scrollVelocityRef.current = 0;
        }, 150);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isCollapsed]);

  const periods = [
    { value: '14d', label: '14d', fullLabel: '14 days' },
    { value: '30d', label: '30d', fullLabel: '30 days' },
    { value: '3m', label: '3m', fullLabel: '3 months' },
    { value: '6m', label: '6m', fullLabel: '6 months' },
    { value: '1y', label: '1y', fullLabel: '1 year' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name', icon: 'A-Z' },
    { value: 'users-desc', label: 'Users', icon: '↓' },
    { value: 'users-asc', label: 'Users', icon: '↑' },
    { value: 'trend', label: 'Trend', icon: '📈' }
  ];

  const handlePeriodClick = (value) => {
    if (value === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      onPeriodChange(value);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      onPeriodChange('custom', customStart, customEnd);
      setShowCustom(false);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    let start, end;

    switch (period) {
      case '14d':
        start = subDays(yesterday, 13);
        end = yesterday;
        break;
      case '30d':
        start = subDays(yesterday, 29);
        end = yesterday;
        break;
      case '3m':
        start = subMonths(yesterday, 3);
        end = yesterday;
        break;
      case '6m':
        start = subMonths(yesterday, 6);
        end = yesterday;
        break;
      case '1y':
        start = subYears(yesterday, 1);
        end = yesterday;
        break;
      case 'custom':
        return `${customStart} — ${customEnd}`;
      default:
        start = subDays(yesterday, 29);
        end = yesterday;
    }

    return `${format(start, 'MMM dd')} — ${format(end, 'MMM dd, yyyy')}`;
  };

  return (
    <div className="control-panel">
      <div className={`control-panel-toggle mobile-only ${isCollapsed ? 'collapsed' : ''}`}>
        <button
          className="toggle-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Show filters' : 'Hide filters'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 5h12M2 8h12M2 11h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="toggle-label">{isCollapsed ? 'Show Filters' : 'Hide Filters'}</span>
        </button>
      </div>
      <div className={`control-panel-inner ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Groups Section */}
        {showGroups && groups && (
          <div className="control-section control-section-groups">
            <div className="section-label">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="8" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="1" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="8" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              Groups
            </div>
            <div className="group-chips">
              <button
                className={`group-chip ${activeGroup === null ? 'active' : ''}`}
                onClick={() => onGroupChange(null)}
              >
                <span className="chip-label">All</span>
                <span className="chip-count">{accountsCount}</span>
              </button>
              <button
                className={`group-chip ${activeGroup === 'ungrouped' ? 'active' : ''}`}
                onClick={() => onGroupChange('ungrouped')}
                style={{ '--chip-color': '#6e7681' }}
              >
                <span className="chip-dot" style={{ background: '#6e7681' }}></span>
                <span className="chip-label">Ungrouped</span>
                <span className="chip-count">{ungroupedAccountsCount}</span>
              </button>
              {groups.filter(g => !g.isDefault).map((group) => (
                <button
                  key={group._id}
                  className={`group-chip ${activeGroup === group._id ? 'active' : ''}`}
                  onClick={() => onGroupChange(group._id)}
                  style={{ '--chip-color': group.color }}
                >
                  <span className="chip-dot" style={{ background: group.color }}></span>
                  <span className="chip-label">{group.name}</span>
                  <span className="chip-count">{group.accountCount}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        {showGroups && groups && <div className="control-divider"></div>}

        {/* Period Section */}
        <div className="control-section control-section-period">
          <div className="section-label">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M7 4v3l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Period
          </div>
          <div className="period-chips">
            {periods.map(p => (
              <button
                key={p.value}
                className={`period-chip ${period === p.value ? 'active' : ''}`}
                onClick={() => handlePeriodClick(p.value)}
                title={p.fullLabel}
              >
                {p.label}
              </button>
            ))}
            <button
              className={`period-chip ${period === 'custom' ? 'active' : ''}`}
              onClick={() => handlePeriodClick('custom')}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <rect x="1" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1 5h10M4 1v2M8 1v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="date-display">
            <span className="date-text">{getDateRange()}</span>
          </div>
          <label className="compare-switch">
            <input
              type="checkbox"
              checked={compareMode}
              onChange={(e) => onCompareModeChange(e.target.checked)}
            />
            <span className="switch-track">
              <span className="switch-thumb"></span>
            </span>
            <span className="switch-label">Compare</span>
          </label>
        </div>

        {/* Divider */}
        {showSort && <div className="control-divider"></div>}

        {/* Sort Section */}
        {showSort && (
          <div className="control-section control-section-sort">
            <div className="section-label">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 4h10M2 7h7M2 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Sort
            </div>
            <div className="sort-chips">
              {sortOptions.map(opt => (
                <button
                  key={opt.value}
                  className={`sort-chip ${sortBy === opt.value ? 'active' : ''}`}
                  onClick={() => onSortChange(opt.value)}
                >
                  <span className="sort-icon">{opt.icon}</span>
                  <span className="sort-label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Custom Date Picker Modal */}
      {showCustom && (
        <div className="custom-picker-overlay" onClick={() => setShowCustom(false)}>
          <div className="custom-picker" onClick={e => e.stopPropagation()}>
            <div className="picker-header">
              <h3>Select Date Range</h3>
              <button className="picker-close" onClick={() => setShowCustom(false)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="picker-body">
              <div className="picker-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div className="picker-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  max={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
            </div>
            <div className="picker-actions">
              <button className="btn btn-secondary" onClick={() => setShowCustom(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCustomApply}>
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ControlPanel;
